export type ReferralAggregate = {
  sourceType: "physician" | "facility";
  sourceName: string;
  period: string;
  orderCount: number;
  uniquePatientCount: number;
};

export type ReferralImportResult = {
  records: ReferralAggregate[];
  sourceRows: number;
  skippedRows: number;
  detected: {
    date: string;
    physician: string;
    facility: string;
    patient: string;
    order: string;
  };
  warnings: string[];
};

function parseCsvRows(text: string) {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (quoted) {
      if (character === '"' && text[index + 1] === '"') {
        field += '"';
        index += 1;
      } else if (character === '"') {
        quoted = false;
      } else {
        field += character;
      }
    } else if (character === '"') {
      quoted = true;
    } else if (character === ",") {
      row.push(field);
      field = "";
    } else if (character === "\n") {
      row.push(field.replace(/\r$/u, ""));
      if (row.some((value) => value.trim())) rows.push(row);
      row = [];
      field = "";
    } else {
      field += character;
    }
  }
  row.push(field.replace(/\r$/u, ""));
  if (row.some((value) => value.trim())) rows.push(row);
  return rows;
}

function normalizedHeader(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]/gu, "");
}

function findHeader(headers: string[], aliases: string[]) {
  const normalizedAliases = aliases.map(normalizedHeader);
  const index = headers.findIndex((header) => normalizedAliases.includes(normalizedHeader(header)));
  return { index, label: index >= 0 ? headers[index] : "" };
}

function periodFromDate(value: string) {
  const cleaned = value.trim();
  if (!cleaned) return "";
  let date: Date;
  if (/^\d{5}(\.\d+)?$/u.test(cleaned)) {
    date = new Date(Date.UTC(1899, 11, 30) + Number(cleaned) * 86400000);
  } else {
    date = new Date(cleaned);
  }
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

const aliases = {
  date: ["Sales Order Confirm Date", "Sales Order Confirmation Date", "Confirmed Date", "Confirm Date", "Order Date", "Sales Order Date"],
  physician: ["Ordering Physician", "Ordering Provider", "Prescribing Physician", "Referring Physician", "Physician", "Doctor"],
  facility: ["Referral Facility", "Referral Source", "Referral Location", "Referring Facility", "Referred By Organization", "Facility"],
  patient: ["Patient ID", "Patient Internal ID", "Brightree Patient ID", "Patient Account Number", "Patient Number"],
  order: ["Sales Order ID", "Sales Order Number", "Sales Order No", "SO Number", "Order Number", "Order ID"],
};

export function aggregateReferralCsv(text: string): ReferralImportResult {
  const rows = parseCsvRows(text.replace(/^\uFEFF/u, ""));
  if (rows.length < 2) throw new Error("The CSV must contain a header row and at least one data row.");
  const headers = rows[0].map((header) => header.trim());
  const date = findHeader(headers, aliases.date);
  const physician = findHeader(headers, aliases.physician);
  const facility = findHeader(headers, aliases.facility);
  const patient = findHeader(headers, aliases.patient);
  const order = findHeader(headers, aliases.order);
  if (date.index < 0) throw new Error("An order or confirmation date column could not be identified.");
  if (physician.index < 0 && facility.index < 0) throw new Error("An ordering physician or referral facility column could not be identified.");

  const groups = new Map<string, { sourceType: "physician" | "facility"; sourceName: string; period: string; orders: Set<string>; patients: Set<string> }>();
  let skippedRows = 0;

  rows.slice(1).forEach((values, rowIndex) => {
    const period = periodFromDate(values[date.index] || "");
    if (!period) {
      skippedRows += 1;
      return;
    }
    const patientToken = patient.index >= 0 ? String(values[patient.index] || "").trim() : "";
    const orderToken = order.index >= 0 ? String(values[order.index] || "").trim() : `row-${rowIndex + 2}`;

    for (const [sourceType, column] of [["physician", physician], ["facility", facility]] as const) {
      if (column.index < 0) continue;
      const sourceName = String(values[column.index] || "").trim();
      if (!sourceName) continue;
      const key = `${sourceType}\u0000${sourceName.toLowerCase()}\u0000${period}`;
      const group = groups.get(key) || { sourceType, sourceName, period, orders: new Set<string>(), patients: new Set<string>() };
      group.orders.add(orderToken || `row-${rowIndex + 2}`);
      if (patientToken) group.patients.add(patientToken);
      groups.set(key, group);
    }
  });

  const warnings: string[] = [];
  if (patient.index < 0) warnings.push("No patient identifier column was detected, so unique-patient totals will be zero.");
  if (order.index < 0) warnings.push("No sales-order number column was detected, so each CSV row will count as one order.");
  if (physician.index < 0) warnings.push("No ordering-physician column was detected.");
  if (facility.index < 0) warnings.push("No referral-facility column was detected.");

  return {
    records: [...groups.values()].map((group) => ({
      sourceType: group.sourceType,
      sourceName: group.sourceName,
      period: group.period,
      orderCount: group.orders.size,
      uniquePatientCount: group.patients.size,
    })),
    sourceRows: rows.length - 1,
    skippedRows,
    detected: {
      date: date.label,
      physician: physician.label,
      facility: facility.label,
      patient: patient.label,
      order: order.label,
    },
    warnings,
  };
}

